import SessionManager from './SessionManager';

interface RequestOptions extends RequestInit {
  skipAuthCheck?: boolean; // Flag to skip automatic auth handling for specific requests
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class HttpInterceptor {
  private static instance: HttpInterceptor;
  private sessionManager: SessionManager | null = null;
  private baseUrl: string = '';

  private constructor() {}

  public static getInstance(): HttpInterceptor {
    if (!HttpInterceptor.instance) {
      HttpInterceptor.instance = new HttpInterceptor();
    }
    return HttpInterceptor.instance;
  }

  /**
   * Initialize the interceptor with session manager
   */
  public initialize(sessionManager: SessionManager, baseUrl?: string): void {
    this.sessionManager = sessionManager;
    this.baseUrl = baseUrl || '';
  }

  /**
   * Enhanced fetch with automatic token handling and session management
   */
  public async fetch<T = any>(url: string, options: RequestOptions = {}): Promise<Response> {
    const { skipAuthCheck, ...fetchOptions } = options;
    
    // Prepare headers
    const headers = new Headers(fetchOptions.headers);
    
    // Add authorization header if token exists and not skipped
    if (!skipAuthCheck) {
      const token = localStorage.getItem('token');
      if (token) {
        // Check if token is expired before making the request
        if (this.sessionManager?.isTokenExpired(token)) {
          console.warn('🔒 Token expired, logging out...');
          this.handleUnauthorized();
          throw new Error('Session expired');
        }
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    // Add default content type if not present
    if (!headers.has('Content-Type') && fetchOptions.body) {
      headers.set('Content-Type', 'application/json');
    }

    // Build full URL
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;

    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        headers
      });

      // Handle different response scenarios
      if (response.status === 401) {
        console.warn('🔒 Received 401 Unauthorized response');
        
        // Try to parse the response to check for SESSION_EXPIRED
        try {
          const clonedResponse = response.clone();
          const errorData = await clonedResponse.json();
          
          // Check if this is a single-session violation
          if (errorData.code === 'SESSION_EXPIRED') {
            console.warn('🚪 Session expired - logged in from another device');
            this.handleUnauthorized('Your account was accessed from another device. For security, only one active session is allowed.');
            return response;
          }
        } catch (e) {
          // Couldn't parse response, continue with normal flow
        }
        
        if (!skipAuthCheck) {
          // Try to refresh token before giving up
          const refreshed = await this.tryRefreshToken();
          if (refreshed) {
            // Retry the original request with new token
            console.log('🔄 Retrying request with refreshed token');
            const retryHeaders = new Headers(fetchOptions.headers);
            const newToken = localStorage.getItem('token');
            if (newToken) {
              retryHeaders.set('Authorization', `Bearer ${newToken}`);
            }
            return fetch(fullUrl, { ...fetchOptions, headers: retryHeaders });
          } else {
            this.handleUnauthorized();
          }
        }
        return response;
      }

      if (response.status === 403) {
        console.warn('🚫 Received 403 Forbidden response');
        // Could handle role-based access here
      }

      // Extend session on successful authenticated requests
      if (response.ok && !skipAuthCheck && localStorage.getItem('token')) {
        this.sessionManager?.extendSession();
      }

      return response;
    } catch (error) {
      console.error('Network error:', error);
      throw error;
    }
  }

  /**
   * Convenience method for JSON responses
   */
  public async fetchJson<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.fetch(url, options);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
    }
    
    return response.json();
  }

  /**
   * GET request
   */
  public async get<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    return this.fetchJson<T>(url, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  public async post<T = any>(url: string, data?: any, options: RequestOptions = {}): Promise<T> {
    return this.fetchJson<T>(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * PUT request
   */
  public async put<T = any>(url: string, data?: any, options: RequestOptions = {}): Promise<T> {
    return this.fetchJson<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * DELETE request
   */
  public async delete<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    return this.fetchJson<T>(url, { ...options, method: 'DELETE' });
  }

  /**
   * PATCH request
   */
  public async patch<T = any>(url: string, data?: any, options: RequestOptions = {}): Promise<T> {
    return this.fetchJson<T>(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Try to refresh the access token
   */
  private async tryRefreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.warn('🚫 No refresh token available');
        return false;
      }

      console.log('🔄 Attempting to refresh access token');
      const response = await fetch('/api/auth/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update tokens
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Extend session if session manager is available
        this.sessionManager?.extendSession();
        
        console.log('✅ Token refreshed successfully');
        return true;
      } else {
        console.warn('❌ Token refresh failed');
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  /**
   * Handle unauthorized responses
   */
  private handleUnauthorized(message?: string): void {
    console.warn('🚪 Handling unauthorized access - logging out');
    
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Notify session manager
    this.sessionManager?.endSession();
    
    // Could emit an event or call a callback here
    window.dispatchEvent(new CustomEvent('session-expired', {
      detail: { 
        reason: message ? 'single_session' : 'unauthorized',
        message: message || 'Your session has expired. Please log in again.'
      }
    }));
  }

  /**
   * Check if a response indicates authentication failure
   */
  public isAuthError(error: any): boolean {
    return error.message?.includes('401') || 
           error.message?.includes('unauthorized') ||
           error.message?.includes('Session expired');
  }

  /**
   * Utility to handle API responses consistently
   */
  public handleApiResponse<T>(response: ApiResponse<T>): T {
    if (response.success) {
      return response.data as T;
    } else {
      throw new Error(response.error || response.message || 'API request failed');
    }
  }

  /**
   * Create a fetch function that automatically uses this interceptor
   */
  public createFetchFunction() {
    return (url: string, options?: RequestOptions) => this.fetch(url, options);
  }

  /**
   * Wrap existing fetch calls to use the interceptor
   */
  public wrapFetch(): void {
    const originalFetch = window.fetch;
    
    window.fetch = (url: string | URL | Request, init?: RequestInit) => {
      // If it's a Request object, URL object, or external URL, use original fetch
      if (typeof url !== 'string' || url.startsWith('http') && !url.includes(this.baseUrl)) {
        return originalFetch(url, init);
      }
      
      // Use our interceptor for internal API calls
      return this.fetch(url, init);
    };
  }

  /**
   * Restore original fetch
   */
  public unwrapFetch(): void {
    // Note: This assumes the original fetch wasn't modified elsewhere
    // In a real app, you might want to store the original fetch reference
    delete (window as any).fetch;
  }
}

export default HttpInterceptor;